// Blog posts are written as content blocks so no Markdown library is needed.
// They are rendered by components/ContentBlocks, which supports p, h2, h3, ul,
// ol, code (with output), tip, callout, table, compare, qa and summary.
// Newer posts live in blogPostsCareerAI.js and blogPostsTech.js.
import { careerAIPosts } from './blogPostsCareerAI';
import { techPosts } from './blogPostsTech';

export const blogCategories = ['Interview Tips', 'Career', 'DSA', 'System Design', 'Agentic AI', 'Engineering'];

const originalPosts = [
  {
    slug: 'how-to-crack-frontend-interviews',
    title: 'How to Crack Front-End Interviews in 2026',
    excerpt: 'The rounds you will face, what interviewers actually look for, and a 6-week prep plan that works.',
    category: 'Interview Tips',
    tags: ['JavaScript', 'React', 'Interviews'],
    author: 'Codeazy Team',
    date: '2026-10-08',
    content: [
      { type: 'p', text: 'Front-end interviews have changed. Most companies now test you on fundamentals, practical UI building and how you reason about trade-offs, not on trivia. Here is what to expect and how to prepare.' },
      { type: 'h2', text: 'The rounds you will usually face' },
      { type: 'ul', items: [
        'JavaScript fundamentals: closures, the event loop, promises, the this keyword, prototypes.',
        'Machine coding: build a working component (autocomplete, modal, infinite scroll) in 60–90 minutes.',
        'DSA: usually easy-to-medium problems on arrays, strings, hash maps and trees.',
        'Front-end system design: design a news feed, a chat UI or a component library.',
        'Behavioural: past projects, conflicts, ownership and impact.'
      ] },
      { type: 'h2', text: 'What interviewers actually look for' },
      { type: 'p', text: 'They want to see how you think. Talk through your approach before writing code, state your assumptions, and handle edge cases such as empty input, slow networks and accessibility.' },
      { type: 'tip', text: 'In machine coding rounds, get a working version first, then improve it. A complete simple solution beats an unfinished perfect one.' },
      { type: 'h2', text: 'Must-know JavaScript implementations' },
      { type: 'p', text: 'Be able to write these from scratch without looking anything up:' },
      { type: 'code', language: 'javascript', code: `function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}` },
      { type: 'ul', items: ['debounce and throttle', 'Promise.all and Promise.race', 'Array.prototype.map, filter and reduce polyfills', 'deep clone and deep equal', 'a simple event emitter'] },
      { type: 'h2', text: 'A 6-week prep plan' },
      { type: 'ol', items: [
        'Weeks 1–2: JavaScript fundamentals and the polyfills above.',
        'Week 3: React in depth: rendering, hooks, state management, performance.',
        'Week 4: Machine coding: build one component every day under a timer.',
        'Week 5: DSA patterns and front-end system design.',
        'Week 6: Mock interviews and behavioural stories.'
      ] }
    ]
  },
  {
    slug: 'dsa-patterns-for-interviews',
    title: '10 DSA Patterns That Solve Most Interview Problems',
    excerpt: 'Stop memorising hundreds of problems. Learn the patterns behind them and recognise them instantly.',
    category: 'DSA',
    tags: ['DSA', 'Algorithms', 'Interviews'],
    author: 'Codeazy Team',
    date: '2026-10-03',
    content: [
      { type: 'p', text: 'Most coding interview questions are variations of a small set of patterns. Once you can recognise the pattern, the solution usually follows.' },
      { type: 'h2', text: 'The patterns' },
      { type: 'ol', items: [
        'Two pointers: sorted arrays, pair sums, removing duplicates.',
        'Sliding window: longest or shortest subarray or substring with a condition.',
        'Fast and slow pointers: cycle detection, middle of a linked list.',
        'Binary search: on sorted data, and "binary search on the answer".',
        'Prefix sums: range sums and subarray sum equals K.',
        'Hash maps: counting, grouping and lookups in O(1).',
        'Monotonic stack: next greater element, histogram problems.',
        'BFS and DFS: trees, grids and graphs.',
        'Heaps: top K elements, merging sorted lists, scheduling.',
        'Dynamic programming: overlapping subproblems with optimal substructure.'
      ] },
      { type: 'h2', text: 'Example: sliding window' },
      { type: 'p', text: 'Find the length of the longest substring without repeating characters:' },
      { type: 'code', language: 'javascript', code: `function longestUniqueSubstring(s) {
  const lastSeen = new Map();
  let start = 0;
  let best = 0;
  for (let end = 0; end < s.length; end++) {
    if (lastSeen.has(s[end]) && lastSeen.get(s[end]) >= start) {
      start = lastSeen.get(s[end]) + 1;
    }
    lastSeen.set(s[end], end);
    best = Math.max(best, end - start + 1);
  }
  return best;
}` },
      { type: 'tip', text: 'Keywords are clues: "contiguous subarray" suggests sliding window or prefix sums, "sorted" suggests two pointers or binary search, and "minimum number of steps" suggests BFS.' },
      { type: 'h2', text: 'How to practise' },
      { type: 'p', text: 'Pick one pattern a week and solve 8–10 problems on it, from easy to hard. After each problem, write one sentence on how you recognised the pattern. That sentence is what you will remember in the interview.' }
    ]
  },
  {
    slug: 'system-design-interview-framework',
    title: 'A Simple Framework for System Design Interviews',
    excerpt: 'A step-by-step structure to answer any "Design X" question confidently in 45 minutes.',
    category: 'System Design',
    tags: ['System Design', 'Scalability', 'Interviews'],
    author: 'Codeazy Team',
    date: '2026-09-26',
    content: [
      { type: 'p', text: 'System design interviews are open-ended on purpose. Having a structure keeps you from rambling and shows the interviewer you can lead a design discussion.' },
      { type: 'h2', text: 'Step 1: Clarify requirements (5 minutes)' },
      { type: 'ul', items: ['Functional: what must the system do?', 'Non-functional: scale, latency, availability, consistency.', 'Out of scope: agree on what you will not design.'] },
      { type: 'h2', text: 'Step 2: Estimate scale (5 minutes)' },
      { type: 'p', text: 'Rough numbers guide your choices. For example, 10 million daily users each making 10 requests is about 100 million requests a day, or roughly 1,200 requests per second on average, with peaks perhaps 3–5 times higher.' },
      { type: 'h2', text: 'Step 3: High-level design (10 minutes)' },
      { type: 'p', text: 'Draw clients, load balancer, services, databases and caches. Walk through one request end to end.' },
      { type: 'h2', text: 'Step 4: Deep dive (15–20 minutes)' },
      { type: 'ul', items: ['Data model and choice of database.', 'Caching strategy and invalidation.', 'Scaling the bottleneck: sharding, replication, queues.', 'Failure handling: retries, idempotency, timeouts.'] },
      { type: 'h2', text: 'Step 5: Wrap up (5 minutes)' },
      { type: 'p', text: 'Summarise the design, call out the trade-offs you made, and mention what you would improve with more time, such as monitoring and alerting.' },
      { type: 'tip', text: 'There is no single right answer. Interviewers score how well you justify trade-offs, not whether you picked the "correct" database.' }
    ]
  },
  {
    slug: 'what-is-agentic-ai',
    title: 'What Is Agentic AI? A Developer’s Introduction',
    excerpt: 'How AI agents differ from chatbots, the agent loop, and the skills you need to build them.',
    category: 'Agentic AI',
    tags: ['AI', 'LLMs', 'Agents'],
    author: 'Codeazy Team',
    date: '2026-09-18',
    content: [
      { type: 'p', text: 'A chatbot answers a question. An agent works towards a goal: it plans, uses tools, looks at the results, and keeps going until the task is done.' },
      { type: 'h2', text: 'The agent loop' },
      { type: 'ol', items: [
        'The model receives a goal and a list of tools it can call.',
        'It decides on the next step, such as searching, reading a file or calling an API.',
        'Your code runs the tool and sends the result back to the model.',
        'The model repeats until it can give a final answer.'
      ] },
      { type: 'code', language: 'javascript', code: `while (true) {
  const response = await callModel(messages, tools);
  if (response.stopReason !== 'tool_use') return response.text;
  const result = await runTool(response.toolCall);
  messages.push(response.message, toolResultMessage(result));
}` },
      { type: 'h2', text: 'Skills you need to build agents' },
      { type: 'ul', items: [
        'Prompt design and structured outputs.',
        'Tool design: clear names, descriptions and input schemas.',
        'Retrieval (RAG) to give the agent the right context.',
        'Evaluation: test sets that show whether a change made the agent better or worse.',
        'Safety: guardrails, permissions and human approval for risky actions.'
      ] },
      { type: 'tip', text: 'Start with a single agent and a few well-designed tools. Most problems do not need a multi-agent system.' }
    ]
  },
  {
    slug: 'resume-tips-for-developers',
    title: 'Resume Tips That Get Developers Shortlisted',
    excerpt: 'Small changes that make recruiters and hiring managers notice your resume.',
    category: 'Career',
    tags: ['Resume', 'Career', 'Job Search'],
    author: 'Codeazy Team',
    date: '2026-09-10',
    content: [
      { type: 'p', text: 'Recruiters spend well under a minute on a first read of a resume. Your job is to make your impact obvious in that time.' },
      { type: 'h2', text: 'Show impact, not duties' },
      { type: 'p', text: 'Instead of "Worked on the checkout page", write "Rebuilt the checkout page in React, cutting load time by 40% and raising conversions by 8%". Use the pattern: action, what you built, measurable result.' },
      { type: 'h2', text: 'Keep it focused' },
      { type: 'ul', items: [
        'One page if you have under 5 years of experience.',
        'Tailor the skills section to the role you are applying for.',
        'List 2–3 strong projects with live links and GitHub repos.',
        'Remove skills you cannot discuss confidently in an interview.'
      ] },
      { type: 'h2', text: 'Projects matter more for freshers' },
      { type: 'p', text: 'If you have little work experience, a deployed full-stack project with real users, authentication and a clean README is worth more than a long list of certificates.' },
      { type: 'tip', text: 'Ask a friend to read your resume for 30 seconds, then tell you what you do. If they cannot, simplify it.' }
    ]
  }
];

// Read time is calculated from the text (about 200 words a minute), so it
// stays accurate when posts are edited
const wordsIn = (block) =>
  [block.text, block.items, block.rows, block.headers, block.question, block.answer, block.bad, block.good, block.code]
    .flat(2)
    .filter((part) => typeof part === 'string')
    .join(' ')
    .split(/\s+/)
    .filter(Boolean).length;

const withReadTime = (post) => ({
  ...post,
  readTime: Math.max(2, Math.round(post.content.reduce((sum, block) => sum + wordsIn(block), 0) / 200))
});

// Newest first; the first post is featured on the blog page
export const blogPosts = [...careerAIPosts, ...techPosts, ...originalPosts]
  .map(withReadTime)
  .sort((a, b) => b.date.localeCompare(a.date));

export const getPostBySlug = (slug) => blogPosts.find((post) => post.slug === slug);
